import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Dialog } from '@/shared/ui/dialogs/Dialog';
import { Input } from '@/shared/ui/inputs/Input';
import { Button } from '@/shared/ui/buttons/Button';
import { useMutate } from '@/shared/hooks/useMutate';
import { useToast } from '@/shared/hooks/useToast';
import { queryKeys } from '@/shared/api/queryKeys';
import { apiPartiesPost } from '@/entities/parties';
import { partyFormSchema, type PartyFormValues } from '@/features/parties/modules/types';

type Props = {
  open: boolean;
  onClose: () => void;
};

export const FeaturesPartiesCreatePartyDialog = ({ open, onClose }: Props) => {
  const { t } = useTranslation();
  const toast = useToast();
  const form = useForm<PartyFormValues>({
    resolver: zodResolver(partyFormSchema),
    defaultValues: { name: '' },
  });

  const nameError = form.formState.errors.name?.message;

  const create = useMutate({
    api: (vals: PartyFormValues) => apiPartiesPost({ name: vals.name }),
    invalidateKeys: [queryKeys.parties.all],
    onSuccess: () => {
      toast.success(t('parties.toast.created'));
      form.reset();
      onClose();
    },
    onError: (_e, message) => toast.error(message),
  });

  return (
    <Dialog
      open={open}
      onClose={() => {
        form.reset();
        onClose();
      }}
      title={t('parties.create.title')}
      description={t('parties.create.description')}
      footer={
        <>
          <Button
            variant="outline"
            onClick={() => {
              form.reset();
              onClose();
            }}
            disabled={create.isPending}
          >
            {t('common.cancel')}
          </Button>
          <Button onClick={form.handleSubmit((v) => create.mutate(v))} loading={create.isPending}>
            {t('parties.create.submit')}
          </Button>
        </>
      }
    >
      <Input
        label={t('parties.form.nameLabel')}
        required
        placeholder={t('parties.form.namePlaceholder')}
        autoFocus
        error={nameError ? t(nameError) : undefined}
        {...form.register('name')}
      />
    </Dialog>
  );
};
